import React, { useState, useEffect } from 'react';
import { Leaf, Sprout, Sun, CloudSun, Flower2, Salad, ChefHat, Home, ArrowRight, Check, DollarSign } from 'lucide-react';
import { allPlants } from '../data/plants-complete';
import { CuratedCollection, getCollectionsForUser, getPlantsForCollection } from '../data/curatedCollections';

interface CuratedCollectionsProps {
  experience: string;
  interests: string[];
  growingMethod: string;
  space: string;
  onSelectCollection: (plantIds: string[]) => void;
}

const collectionIcons: Record<string, React.ReactNode> = {
  // Beginner friendly
  'beginners-dozen': <Sprout className="w-8 h-8" />,
  'thirty-day-challenge': <Sun className="w-8 h-8" />,
  'kids-garden': <Leaf className="w-8 h-8" />,
  'budget-saver': <DollarSign className="w-8 h-8" />,
  
  // By cooking style
  'salsa-garden': <ChefHat className="w-8 h-8" />,
  'italian-garden': <Salad className="w-8 h-8" />,
  'asian-stir-fry': <Leaf className="w-8 h-8" />,
  'mediterranean-garden': <Sun className="w-8 h-8" />,
  'taco-night': <ChefHat className="w-8 h-8" />,
  'indian-curry': <Sun className="w-8 h-8" />,
  
  // By purpose
  'salad-bowl': <Salad className="w-8 h-8" />,
  'herb-garden': <Leaf className="w-8 h-8" />,
  'canning-garden': <Home className="w-8 h-8" />,
  'freezer-garden': <CloudSun className="w-8 h-8" />,
  'cut-and-come-again': <Leaf className="w-8 h-8" />,
  'cocktail-garden': <Sun className="w-8 h-8" />,
  'tea-garden': <Leaf className="w-8 h-8" />,
  
  // By challenge/goal
  'container-garden': <Home className="w-8 h-8" />,
  'raised-bed-starter': <Home className="w-8 h-8" />,
  'high-yield': <Sun className="w-8 h-8" />,
  
  // By season
  'spring-early': <Flower2 className="w-8 h-8" />,
  'summer-bounty': <Sun className="w-8 h-8" />,
  'fall-harvest': <Leaf className="w-8 h-8" />,
  'winter-hardy': <CloudSun className="w-8 h-8" />,
  
  // By growing conditions
  'tomato-lovers': <Sun className="w-8 h-8" />,
  'heat-warriors': <Sun className="w-8 h-8" />,
  'shade-tolerant': <CloudSun className="w-8 h-8" />,
  
  // Special diets
  'keto-friendly': <Leaf className="w-8 h-8" />,
  'gluten-free': <Leaf className="w-8 h-8" />,
  'protein-power': <Sun className="w-8 h-8" />,
  
  // Aesthetic/theme
  'rainbow-garden': <Sun className="w-8 h-8" />,
  'butterfly-garden': <Flower2 className="w-8 h-8" />,
  'bee-sanctuary': <Flower2 className="w-8 h-8" />,
  'pollinator-paradise': <Flower2 className="w-8 h-8" />,
  'three-sisters': <Leaf className="w-8 h-8" />,
};

const experienceLabels: Record<string, string> = {
  novice: 'First Garden',
  intermediate: 'Some Experience',
  expert: 'Expert Gardener',
};

export function CuratedCollections({ 
  experience, 
  interests, 
  growingMethod, 
  space, 
  onSelectCollection 
}: CuratedCollectionsProps) {
  const [collections, setCollections] = useState<CuratedCollection[]>([]);
  const [selectedCollection, setSelectedCollection] = useState<string | null>(null);
  const [collectionPlants, setCollectionPlants] = useState<Record<string, any[]>>({});

  useEffect(() => {
    const matchedCollections = getCollectionsForUser(experience, interests, growingMethod, space);
    setCollections(matchedCollections);
    
    // Pre-load plants for each collection
    const plants: Record<string, any[]> = {};
    matchedCollections.forEach(collection => {
      plants[collection.id] = getPlantsForCollection(collection, allPlants);
    });
    setCollectionPlants(plants);
  }, [experience, interests, growingMethod, space]);

  const handleSelectCollection = (collection: CuratedCollection) => {
    setSelectedCollection(collection.id);
    const plants = collectionPlants[collection.id] || [];
    onSelectCollection(plants.map(p => p.id));
  };

  if (collections.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
        <Leaf className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>Complete your garden preferences to see curated collections!</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1a1a1a', marginBottom: '0.5rem' }}>
          🎯 Curated Collections for You
        </h2>
        <p style={{ color: '#666' }}>
          Based on your profile: <strong>{experienceLabels[experience]}</strong> • {interests.join(', ')}
        </p>
      </div>

      {/* Collections Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
        gap: '1rem' 
      }}>
        {collections.map(collection => {
          const plants = collectionPlants[collection.id] || [];
          const isSelected = selectedCollection === collection.id;
          
          return (
            <div
              key={collection.id}
              onClick={() => handleSelectCollection(collection)}
              style={{
                padding: '1.25rem',
                borderRadius: '12px',
                border: `2px solid ${isSelected ? '#22c55e' : '#e5e7eb'}`,
                background: isSelected ? '#f0fdf4' : 'white',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: isSelected ? '0 4px 12px rgba(34, 197, 94, 0.2)' : '0 2px 4px rgba(0,0,0,0.05)',
              }}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = '#22c55e';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.transform = 'translateY(0)';
                }
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                <div style={{ 
                  padding: '0.75rem', 
                  borderRadius: '10px', 
                  background: isSelected ? '#22c55e' : '#f3f4f6',
                  color: isSelected ? 'white' : '#6b7280'
                }}>
                  {collectionIcons[collection.id] || <Leaf className="w-8 h-8" />}
                </div>
                
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                    <span style={{ fontSize: '1.25rem' }}>{collection.emoji}</span>
                    <h3 style={{ fontWeight: 'bold', color: '#1a1a1a' }}>{collection.name}</h3>
                    {isSelected && <Check className="w-5 h-5 text-green-600" />}
                  </div>
                  
                  <p style={{ fontSize: '0.875rem', color: '#666', marginBottom: '0.75rem' }}>
                    {collection.description}
                  </p>
                  
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
                    {collection.tags.slice(0, 3).map(tag => (
                      <span
                        key={tag}
                        style={{
                          fontSize: '0.75rem',
                          padding: '0.25rem 0.5rem',
                          background: '#f3f4f6',
                          borderRadius: '4px',
                          color: '#666'
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    paddingTop: '0.75rem',
                    borderTop: '1px solid #e5e7eb'
                  }}>
                    <span style={{ fontSize: '0.875rem', color: '#666' }}>
                      {plants.length} plants
                    </span>
                    <span style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.25rem',
                      fontSize: '0.875rem',
                      color: isSelected ? '#22c55e' : '#666',
                      fontWeight: isSelected ? 'bold' : 'normal'
                    }}>
                      {isSelected ? 'Selected' : 'View'}
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Selected Collection Details */}
      {selectedCollection && collectionPlants[selectedCollection] && (
        <div style={{ 
          marginTop: '1.5rem', 
          padding: '1.25rem', 
          background: '#f0fdf4', 
          borderRadius: '12px',
          border: '2px solid #22c55e'
        }}>
          <h3 style={{ fontWeight: 'bold', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Check className="w-5 h-5 text-green-600" />
            {collections.find(c => c.id === selectedCollection)?.name} - Plants
          </h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
            gap: '0.5rem' 
          }}>
            {collectionPlants[selectedCollection].slice(0, 12).map(plant => (
              <div 
                key={plant.id}
                style={{
                  padding: '0.5rem 0.75rem',
                  background: 'white',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <span style={{ fontSize: '1rem' }}>
                  {plant.category === 'herb' ? '🌿' : 
                   plant.category === 'vegetable' ? '🥕' : 
                   plant.category === 'fruit' ? '🍓' : 
                   plant.category === 'flower' ? '🌻' : '🌱'}
                </span>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {plant.name}
                </span>
              </div>
            ))}
            {collectionPlants[selectedCollection].length > 12 && (
              <div style={{ 
                padding: '0.5rem 0.75rem',
                background: 'rgba(255,255,255,0.5)',
                borderRadius: '6px',
                fontSize: '0.875rem',
                color: '#666',
                textAlign: 'center'
              }}>
                +{collectionPlants[selectedCollection].length - 12} more
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
